import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Modal from "@mui/material/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { InputField } from "../../common/theme/Input";
import { SelectField } from "../../common/theme/select";
import { RadioInputs } from "../../common/theme/RadioInput";
import AppButton from "../../common/theme/Button";
import checkoutStyles from "../../scss/pageComponent/checkoutCart.module.scss";
import commonStyles from "../../scss/common/common.module.scss";
import {
  CustomQuestionAnswer,
  getCustomQuestionLabel,
  getStoreCustomInformations,
  isCustomQuestionRequired,
} from "../../common/utils/cartLineHelpers";

const modalBoxStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(640px, calc(100vw - 32px))",
  maxHeight: "90vh",
  overflow: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  border: "1px solid #E6E8ED",
  borderRadius: "8px",
};

export type BundleCheckoutPlayerValues = {
  firstName: string;
  lastName: string;
  teamId: string;
  jerseyNumber: string;
  grad_year: string;
  last_name: string;
  misc: string;
  customAnswers?: CustomQuestionAnswer[];
};

interface IProps {
  open: boolean;
  onClose: () => void;
  storeDetail: any;
  onSave: (values: BundleCheckoutPlayerValues) => void;
  initialValues?: Partial<BundleCheckoutPlayerValues>;
}

const buildFormValues = (
  customInformations: any[],
  initial?: Partial<BundleCheckoutPlayerValues>
) => {
  const values: Record<string, any> = {
    firstName: initial?.firstName ?? "",
    lastName: initial?.lastName ?? "",
    teamId: initial?.teamId ?? "",
    jerseyNumber: initial?.jerseyNumber ?? "",
    grad_year: initial?.grad_year ?? "",
    last_name: initial?.last_name ?? "",
    misc: initial?.misc ?? "",
  };

  const answersById = new Map(
    (initial?.customAnswers ?? []).map((answer) => [
      String(answer.id),
      answer.value ?? "",
    ])
  );

  customInformations.forEach((item: any) => {
    const key = String(item.id);
    values[key] = answersById.has(key) ? answersById.get(key) : "";
  });

  return values;
};

const BundlePlayerInfoModal = ({
  open,
  onClose,
  storeDetail,
  onSave,
  initialValues,
}: IProps) => {
  const [teamList, setTeamList] = useState<{ value: string; title: string }[]>(
    []
  );

  const customInformations = useMemo(
    () => getStoreCustomInformations(storeDetail),
    [storeDetail]
  );

  const formInitialValues = useMemo(
    () => buildFormValues(customInformations, initialValues),
    [customInformations, initialValues]
  );

  const validationSchema = useMemo(() => {
    const shape: Record<string, yup.AnySchema> = {
      firstName: yup
        .string()
        .trim()
        .max(20, "Maximum 20 characters")
        .required("Please enter your first name"),
      lastName: yup
        .string()
        .trim()
        .max(20, "Maximum 20 characters")
        .required("Please enter your last name"),
      teamId:
        storeDetail?.is_team_available === 1
          ? yup.string().trim().required("Please select a team")
          : yup.string().trim(),
      jerseyNumber:
        storeDetail?.is_jersey_number_required === 1
          ? yup.string().trim().required("Please enter your jersey number")
          : yup.string().trim(),
      grad_year:
        storeDetail?.is_grad_year_required === 1
          ? yup.string().trim().required("Please enter your grad year")
          : yup.string().trim(),
      last_name:
        storeDetail?.is_last_name_required === 1
          ? yup.string().trim().required("Please enter your last name")
          : yup.string().trim(),
      misc:
        storeDetail?.is_misc_required === 1
          ? yup.string().trim().required("Please enter your misc")
          : yup.string().trim(),
    };

    customInformations.forEach((item: any) => {
      const key = String(item.id);
      shape[key] = isCustomQuestionRequired(item)
        ? yup
            .string()
            .trim()
            .max(50, "Maximum 50 characters")
            .required("This field is required")
        : yup.string().max(50, "Maximum 50 characters");
    });

    return yup.object().shape(shape);
  }, [storeDetail, customInformations]);

  const formik = useFormik({
    initialValues: formInitialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave({
        firstName: values.firstName,
        lastName: values.lastName,
        teamId: values.teamId,
        jerseyNumber: values.jerseyNumber,
        grad_year: values.grad_year,
        last_name: values.last_name,
        misc: values.misc,
        customAnswers: customInformations.map((item: any) => ({
          id: item.id,
          value: values[item.id] ?? values[String(item.id)] ?? "",
        })),
      });
    },
  });

  useEffect(() => {
    const teams = storeDetail?.teams?.map((item: any) => ({
      title: item.team_name,
      value: item.id.toString(),
    }));
    setTeamList(teams ?? []);
  }, [storeDetail]);

  useEffect(() => {
    if (!open) {
      formik.resetForm({
        values: formInitialValues,
      });
    }
  }, [open, formInitialValues]);

  const handleRadioChange = (e: any) => {
    formik.setFieldValue(e.target.name, e.target.value);
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="bundle-player-modal">
      <Box sx={modalBoxStyle}>
        <h2
          style={{
            margin: "0 0 16px",
            fontSize: "22px",
            fontWeight: 700,
            color: "#1a2b4a",
          }}
        >
          Customer Information
        </h2>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={2}
            className={checkoutStyles.CheckoutCart}
          >
            <Grid item lg={6} xs={12}>
              <InputField
                formik={formik}
                label="Player First Name"
                placeholder="Enter player first name"
                name="firstName"
                required
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <InputField
                formik={formik}
                label="Player Last Name"
                placeholder="Enter player last name"
                name="lastName"
                required
              />
            </Grid>
            {storeDetail?.is_team_available === 1 ? (
              <Grid item lg={6} xs={12}>
                <SelectField
                  label="Select Team"
                  name="teamId"
                  placeholder="Select"
                  formik={formik}
                  options={teamList ?? []}
                  required
                />
              </Grid>
            ) : null}
            {storeDetail?.is_jersey_number_required === 1 ? (
              <Grid item lg={6} xs={12}>
                <InputField
                  formik={formik}
                  label={
                    storeDetail?.jersey_number_question ?? "Jersey Number"
                  }
                  placeholder={
                    storeDetail?.jersey_number_placeholder ??
                    "Enter your jersey number"
                  }
                  name="jerseyNumber"
                  required
                />
              </Grid>
            ) : null}
            {storeDetail?.is_grad_year_required === 1 ? (
              <Grid item lg={6} xs={12}>
                <InputField
                  formik={formik}
                  label={
                    storeDetail?.grad_year_question ?? "Grad Year"
                  }
                  placeholder={
                    storeDetail?.grad_year_placeholder ??
                    "Enter your grad year"
                  }
                  name="grad_year"
                  required
                />
              </Grid>
            ) : null}
            {storeDetail?.is_last_name_required === 1 ? (
              <Grid item lg={6} xs={12}>
                <InputField
                  formik={formik}
                  label={storeDetail?.last_name_question ?? "Last name"}
                  placeholder={
                    storeDetail?.last_name_placeholder ??
                    "Enter your last name"
                  }
                  name="last_name"
                  required
                />
              </Grid>
            ) : null}
            {storeDetail?.is_misc_required === 1 ? (
              <Grid item lg={6} xs={12}>
                <InputField
                  formik={formik}
                  label={storeDetail?.misc_question ?? "Misc"}
                  placeholder={
                    storeDetail?.misc_placeholder ?? "Enter your misc"
                  }
                  name="misc"
                  required
                />
              </Grid>
            ) : null}
            {customInformations.map((val: any) => {
              const label = getCustomQuestionLabel(val);
              const required = isCustomQuestionRequired(val);
              const fieldName = String(val.id);
              const selectOptions = (val.custom_information_options || []).map(
                (option: any) => ({
                  title: option.name,
                  value: option.name,
                })
              );

              if (val.input_type === 1) {
                return (
                  <Grid item lg={6} xs={12} key={fieldName}>
                    <SelectField
                      label={label}
                      name={fieldName}
                      placeholder="Select"
                      formik={formik}
                      options={selectOptions}
                      required={required}
                    />
                  </Grid>
                );
              }

              if (val.input_type === 2) {
                return (
                  <Grid item lg={6} xs={12} key={fieldName}>
                    <InputField
                      formik={formik}
                      type="text"
                      label={label}
                      placeholder=""
                      name={fieldName}
                      required={required}
                    />
                  </Grid>
                );
              }

              if (val.input_type === 3) {
                return (
                  <Grid item lg={6} xs={12} key={fieldName}>
                    <InputField
                      formik={formik}
                      label={label}
                      name={fieldName}
                      textFieldProps={{ multiline: true, rows: 3 }}
                      required={required}
                    />
                  </Grid>
                );
              }

              if (val.input_type === 4) {
                return (
                  <Grid item lg={6} xs={12} key={fieldName}>
                    <RadioInputs
                      formik={formik}
                      label={label}
                      name={fieldName}
                      onChange={handleRadioChange}
                      options={[
                        { label: "Yes", value: "1" },
                        { label: "No", value: "0" },
                      ]}
                      required={required}
                    />
                  </Grid>
                );
              }

              return null;
            })}
          </Grid>
          <div className={commonStyles.shoppingChat_checkoutBtn}>
            <AppButton type="submit" className={commonStyles.continue}>
              Save & Continue
            </AppButton>
          </div>
        </form>
      </Box>
    </Modal>
  );
};

export default BundlePlayerInfoModal;
